import { createSlice } from '@reduxjs/toolkit';
import { ApiKeyDto, CreateApiKeyResponse, ApiKeyUsageStats, RotateApiKeyResponse } from '../../services/api/apiKeyService';
import {
  fetchApiKeys,
  createApiKey,
  revokeApiKey,
  fetchApiKeyUsage,
  rotateApiKey,
} from '../thunks/apiKeyThunks';

export interface ApiKeyState {
  apiKeys: ApiKeyDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
  createdKey: CreateApiKeyResponse | null;
  rotatedKey: RotateApiKeyResponse | null;
  usageStats: ApiKeyUsageStats | null;
  usageLoading: boolean;
}

const initialState: ApiKeyState = {
  apiKeys: [],
  totalCount: 0,
  page: 1,
  pageSize: 10,
  loading: false,
  error: null,
  createdKey: null,
  rotatedKey: null,
  usageStats: null,
  usageLoading: false,
};

const apiKeySlice = createSlice({
  name: 'apiKey',
  initialState,
  reducers: {
    clearCreatedKey: (state) => {
      state.createdKey = null;
    },
    clearRotatedKey: (state) => {
      state.rotatedKey = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApiKeys.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApiKeys.fulfilled, (state, action) => {
        state.loading = false;
        state.apiKeys = action.payload.apiKeys;
        state.totalCount = action.payload.totalCount;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchApiKeys.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(createApiKey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createApiKey.fulfilled, (state, action) => {
        state.loading = false;
        state.createdKey = action.payload;
      })
      .addCase(createApiKey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(revokeApiKey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(revokeApiKey.fulfilled, (state, action) => {
        state.loading = false;
        state.apiKeys = state.apiKeys.filter((k) => k.id !== action.payload);
      })
      .addCase(revokeApiKey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchApiKeyUsage.pending, (state) => {
        state.usageLoading = true;
        state.error = null;
      })
      .addCase(fetchApiKeyUsage.fulfilled, (state, action) => {
        state.usageLoading = false;
        state.usageStats = action.payload;
      })
      .addCase(fetchApiKeyUsage.rejected, (state, action) => {
        state.usageLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(rotateApiKey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rotateApiKey.fulfilled, (state, action) => {
        state.loading = false;
        state.rotatedKey = action.payload;
      })
      .addCase(rotateApiKey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCreatedKey, clearRotatedKey, clearError } = apiKeySlice.actions;
export default apiKeySlice.reducer;
